<script>
  import { cubicInOut } from "svelte/easing";
  import { fly } from "svelte/transition";
  import CalendarHero from "$lib/components/Calendar/CalendarHero.svelte";
  import { EventGrid, EventList } from "@ObamaFoundation/of-design-system";

  import { transformAndOrganizeEvents } from "$lib/utilities/calendar";
  import {
    getTodayDate,
    generateMonthOptions,
    getInitialSelectedMonth,
    getVisibleMonths,
    getBoundaryDate
  } from "$lib/utilities/eventDate.js";
  import { filterItemsStore } from "$lib/stores.js";
  import {
    collectFilterCategoriesFromItems,
    filterEvents,
    filterPastEvents
  } from "$lib/utilities/eventFilters.js";
  import { getTotalNumberOfSelectedFilters, tagGroupsToFilters } from "$lib/utilities/filters.js";
  import FilterList from "$lib/components/FilterList.svelte";
  import CalendarEmpty from "$lib/components/Calendar/CalendarEmpty.svelte";
  import CalendarNextButton from "$lib/components/Calendar/CalendarNextButton.svelte";

  export let listItems = [];
  export let featuredItems = [];
  export let themedItems = [];

  const todayDate = getTodayDate();

  // Filter out past events
  const futureListItems = filterPastEvents(listItems, todayDate);
  const futureFeaturedItems = filterPastEvents(featuredItems, todayDate);

  let filterListIsOpen = false;
  $: activeFilters = $filterItemsStore;
  $: numActiveFilters = getTotalNumberOfSelectedFilters(activeFilters);
  $: areFiltersApplied = numActiveFilters > 0;
  $: rawFilters = {
    fields: {
      internalTitle: "Generated Filters",
      items: tagGroupsToFilters(collectFilterCategoriesFromItems(futureListItems))
    }
  };

  const earliestEventDate = getBoundaryDate(listItems, false);

  // Use whichever comes later: today or the earliest event date
  const startDate =
    earliestEventDate && earliestEventDate > todayDate ? earliestEventDate : todayDate;

  let selectedMonth = getInitialSelectedMonth(startDate);
  let selectedFormat = "view-as-grid";

  $: years = generateMonthOptions(startDate, listItems);
  $: visibleMonths = getVisibleMonths(selectedMonth, todayDate);
  $: allMonthValues = years.flatMap((year) => year.months.map((m) => m.value));
  $: isLastMonth = selectedMonth === allMonthValues[allMonthValues.length - 1];

  $: filteredItems = filterEvents(futureListItems, activeFilters);
  $: filteredFeaturedItems = filterEvents(futureFeaturedItems, activeFilters);

  $: organizedEvents = transformAndOrganizeEvents({
    selectedFormat,
    items: filteredItems,
    visibleMonths,
    themedItems,
    featuredItems: filteredFeaturedItems,
    hasFiltersActive: numActiveFilters > 0
  });
</script>

{#if activeFilters.length > 0}
  <CalendarHero
    bind:selectedFormat
    bind:filterListIsOpen
    bind:selectedMonth
    {activeFilters}
    {years}
  />

  {#key selectedMonth}
    <div class="mt-10 mb-25 md:mt-6 md:mb-30 lg:mt-7 lg:mb-40 xl:mt-10 xl:mb-50">
      {#if organizedEvents.length === 0}
        <CalendarEmpty hasNextMonth={!isLastMonth} {areFiltersApplied} />
      {:else if selectedFormat === "view-as-list"}
        {#each organizedEvents as week, idx}
          {#if week.items.length === 0}
            <CalendarEmpty hasNextMonth={!isLastMonth} {areFiltersApplied} />
          {:else}
            <EventList
              heading={week.heading}
              items={week.items}
              hasFeaturedItem={week.hasFeaturedItem}
              hasDivider={idx !== 0 && !week.hasFeaturedItem}
            />
          {/if}
        {/each}
      {:else}
        {#each organizedEvents as month, idx}
          <div>
            {#if idx > 0}
              <div
                in:fly|global={{ duration: 500, ease: cubicInOut, y: 30, delay: 500 }}
                out:fly|global={{ duration: 500, ease: cubicInOut, y: -20, delay: 100 }}
              >
                <CalendarHero
                  selectedMonth={month.date}
                  containerClasses="!pt-20 !md:pt-30 !lg:pt-40 pb-6"
                  title={month.title}
                  isStaticHero={true}
                />
              </div>
            {/if}
            {#if month.items.length === 0}
              <CalendarEmpty hasNextMonth={!isLastMonth} {areFiltersApplied} />
            {:else}
              <EventGrid hasFeaturedItem={month.hasFeaturedItem} items={month.items} />
            {/if}
          </div>
        {/each}
      {/if}
      {#if !isLastMonth}
        <CalendarNextButton bind:selectedMonth {selectedFormat} />
      {/if}
    </div>
  {/key}
{/if}

<FilterList {rawFilters} bind:filterListIsOpen shouldUseQueryParams={true} />
